import { useEffect, useRef, useState } from "react";
import {
  RequestApproveUserRequest,
  RequestApproveUserRequestWrapper,
  RequestDeleteUserRequest,
  RequestDeleteUserRequestWrapper,
  RequestGetReportItemsCsvListRequest,
  RequestGetReportPlansCsvListRequest,
  RequestGetUserListRequest,
  RequestUpdateUserRequest,
  ResponseGetUserListResonse,
  ResponsePlanItem,
  ResponseReportItem,
} from "../../services/OpenAPI";
import { ja as jaLocale } from "date-fns/locale";
import { loginUserSelector } from "../../recoil/selectors/loginUserSelector";
import { Permission } from "../../constants/roles";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { useApiGetUserList } from "../api/useApiGetUserList";
import { UserListItem } from "../../components/Table/UserListTable";
import { useApiUpdateUser } from "../api/useApiUpdateUser";
import { userUpdatedFlagState } from "../../recoil/atoms/updateUserAtoms";
import { useApiApproveUser } from "../api/useApiApproveUser";
import { useApiDeleteUser } from "../api/useApiDeleteUser";
import { format, subMonths } from "date-fns";
import { SelectChangeEvent } from "@mui/material";
import { useApiGetReportItemsCsvList } from "../api/useApiGetReportItemsCsvList";
import { exportToCSV } from "../../utils/csvExporter";
import {
  reportItemCSVFields,
  reportPlanCSVFields,
} from "../../utils/csvExportConfig";
import { useApiGetReportPlansCsvList } from "../api/useApiGetReportPlansCsvList";

export type SearchGenreOption = {
  id: string;
  label: string;
};

export const useReport = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { fetchUserList } = useApiGetUserList();
  const { updateUser } = useApiUpdateUser();
  const { approveUsers } = useApiApproveUser();
  const { deleteUsers } = useApiDeleteUser();
  const { fetchReportItemCsvList } = useApiGetReportItemsCsvList();
  const { fetchReportPlanCsvList } = useApiGetReportPlansCsvList();
  const loginUser = useRecoilValue(loginUserSelector);
  const setUserUpdatedFlag = useSetRecoilState(userUpdatedFlagState);
  const userUpdatedFlag = useRecoilValue(userUpdatedFlagState);
  const reserUserUpdatedFlag = useResetRecoilState(userUpdatedFlagState);

  const [userList, setUserList] = useState<UserListItem[]>([]);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<UserListItem | null>(null);

  const [selectedMonth, setSelectedMonth] = useState(() =>
    format(new Date(), "yyyy年MM月", { locale: jaLocale })
  );

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const handleCloseToast = () => setToastOpen(false);

  const handleCheckChange = (id: string) => {
    setCheckedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleCheckChangeAll = (newCheckedIds: Set<string>) => {
    setCheckedIds(newCheckedIds);
  };

  // 過去24ヶ月分の「yyyy年MM月」リストを作成
  const monthOptions = [
    "All",
    ...Array.from({ length: 24 }, (_, i) =>
      format(subMonths(new Date(), i), "yyyy年MM月", { locale: jaLocale })
    ),
  ];

  const handleMonthChange = (event: SelectChangeEvent<string>) => {
    setSelectedMonth(event.target.value);
  };

  const handleItemCsv = async () => {
    if (!loginUser || !loginUser.tenantId || !loginUser.id) return;
    const req: RequestGetReportItemsCsvListRequest = {
      tenantId: loginUser?.tenantId,
      shippedDate:
        selectedMonth === "All"
          ? undefined
          : `${selectedMonth.slice(0, 4)}-${selectedMonth.slice(5, 7)}-01`,
    };

    const response = await fetchReportItemCsvList(req);
    if (response) {
      if (!response.items) return;

      exportToCSV<ResponseReportItem>(
        response.items as ResponseReportItem[],
        reportItemCSVFields,
        "report_items"
      );
    }
  };

  const handlePlanCsv = async () => {
    if (!loginUser || !loginUser.tenantId || !loginUser.id) return;
    const req: RequestGetReportPlansCsvListRequest = {
      tenantId: loginUser?.tenantId,
      shippedDate:
        selectedMonth === "All"
          ? undefined
          : `${selectedMonth.slice(0, 4)}-${selectedMonth.slice(5, 7)}-01`,
    };
    const response = await fetchReportPlanCsvList(req);
    if (response) {
      if (!response.items) return;

      exportToCSV<ResponsePlanItem>(
        response.items as ResponsePlanItem[],
        reportPlanCSVFields,
        "report_plans"
      );
    }
  };

  const transformData = (
    userList: ResponseGetUserListResonse[] | undefined
  ): UserListItem[] => {
    if (!Array.isArray(userList)) return [];
    const result: UserListItem[] = [];

    userList.forEach((user) => {
      const baseIds =
        user.baseInfo?.map((b) => b.id).filter((id): id is number => !!id) ??
        [];
      const baseNames =
        user.baseInfo
          ?.map((b) => b.name)
          .filter((name): name is string => !!name) ?? [];

      result.push({
        id: Number(user.id),
        lastName: user.lastName,
        firstName: user.firstName,
        mailAddress: user.mail,
        base: baseNames.join(", "),
        baseIds: baseIds,
        shopName: user.shopName,
        roomId: user.chatworkRoomId,
        driverName: user.driverName,
        memo: user.introducer,
      });
    });

    return result;
  };

  const handleUpdate = async (updatedUser: UserListItem, baseIds: number[]) => {
    if (
      !loginUser ||
      !loginUser.tenantId ||
      !loginUser.id ||
      !loginUser.permissionId
    )
      return;

    const request: RequestUpdateUserRequest = {
      permissionId: loginUser.permissionId,
      tenantId: loginUser.tenantId,
      id: Number(updatedUser.id),
      lastName: updatedUser.lastName,
      firstName: updatedUser.firstName,
      mail: updatedUser.mailAddress,
      baseId: Number(updatedUser.baseId),
      shopName: updatedUser.shopName,
      chatworkRoomId: updatedUser.roomId,
      driverName: updatedUser.driverName,
      introducer: updatedUser.memo,
      baseIds: baseIds,
    };
    const result = await updateUser(request);
    if (result) {
      setToastMessage("更新が完了しました");
      setToastSeverity("success");
      setToastOpen(true);
      setUserUpdatedFlag(true);
    } else {
      setToastMessage("更新に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const handleRowClick = async (user: UserListItem) => {
    setSelectedRow(user);
    setDetailDialogOpen(true);
  };

  const handleApprove = async () => {
    if (checkedIds.size === 0) return;

    // 選択されたユーザー情報をまとめる
    const selectedUsers = userList.filter((user) =>
      checkedIds.has(String(user.id))
    );

    // APIのリクエスト形式にマッピング
    const requestPayload: RequestApproveUserRequest[] = selectedUsers.map(
      (user) => ({
        id: Number(user.id),
        tenantId: loginUser?.tenantId,
        updatedUserId: loginUser?.id,
        baseId: user.baseId,
      })
    );
    const requestPayloadWrapper: RequestApproveUserRequestWrapper = {
      req: requestPayload,
    };

    const result = await approveUsers(requestPayloadWrapper);
    if (result) {
      setToastMessage("承認が完了しました");
      setToastSeverity("success");
      setToastOpen(true);
      setUserUpdatedFlag(true);
    } else {
      setToastMessage("承認に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const handleCancel = () => {
    setCheckedIds(new Set());
  };

  const handleDelete = async () => {
    if (checkedIds.size === 0) return;

    // 選択されたユーザー情報をまとめる
    const selectedUsers = userList.filter((user) =>
      checkedIds.has(String(user.id))
    );

    // APIのリクエスト形式にマッピング
    const requestPayload: RequestDeleteUserRequest[] = selectedUsers.map(
      (user) => ({
        id: Number(user.id),
        tenantId: loginUser?.tenantId,
        updatedUserId: loginUser?.id,
        baseId: user.baseId,
      })
    );

    const requestPayloadWrapper: RequestDeleteUserRequestWrapper = {
      req: requestPayload,
    };

    const result = await deleteUsers(requestPayloadWrapper);
    if (result) {
      setToastMessage("ユーザー削除に成功しました");
      setToastSeverity("success");
      setToastOpen(true);
      setCheckedIds(new Set());
      setUserUpdatedFlag(true);
    } else {
      setToastMessage("ユーザー削除に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const fetchData = async (req: RequestGetUserListRequest) => {
    const response = await fetchUserList(req);
    if (response) {
      const transformed = transformData(response);
      setUserList(transformed);
    }
  };

  useEffect(() => {
    if (userUpdatedFlag) {
      if (loginUser?.tenantId && loginUser?.id) {
        const req: RequestGetUserListRequest = {
          tenantId: loginUser.tenantId,
          baseId: loginUser.baseId!,
          permissionId: Permission.General,
          userId: loginUser.id,
        };
        fetchData(req);
      }
      reserUserUpdatedFlag();
      setDetailDialogOpen(false);
    }
  }, [userUpdatedFlag]);

  useEffect(() => {
    if (loginUser?.tenantId && loginUser?.id) {
      const req: RequestGetUserListRequest = {
        tenantId: loginUser.tenantId,
        baseId: loginUser.baseId!,
        permissionId: Permission.General,
        userId: loginUser.id,
      };
      fetchData(req);
    }
  }, []);

  return {
    inputRef,
    userList,
    selectedRow,
    checkedIds,
    handleCheckChange,
    handleUpdate,
    handleRowClick,
    detailDialogOpen,
    setDetailDialogOpen,
    toastOpen,
    toastMessage,
    toastSeverity,
    handleCloseToast,
    handleApprove,
    handleCancel,
    handleDelete,
    handleCheckChangeAll,
    selectedMonth,
    setSelectedMonth,
    monthOptions,
    handleMonthChange,
    handleItemCsv,
    handlePlanCsv,
  };
};
